import React from "react";
import { useImmer } from "use-immer";

import { IgButton } from "@intelligems/ig-theme";
import { StripeSubscriptionSummaryDto } from "@intelligems/ig-types";
import { useGetOrganizationSummary } from "@src/api/organization-summaries/get-organization-summary";
import { useCreateUsageBillingUrl } from "@src/api/shopify-app-subscriptions/create-usage-billing-url";
import { useGetBillingPortal } from "@src/api/stripe/get-billing-portal";

import { IgInfo } from "../../Utils/IgInfo";

interface MissingBillingAlertProps {
  missingBillings: StripeSubscriptionSummaryDto[];
}

export const MissingBillingAlert = ({
  missingBillings,
}: MissingBillingAlertProps) => {
  const [updatePaymentMethodClicked, setUpdatePaymentMethod] =
    useImmer<boolean>(false);
  const getBillingPortal = useGetBillingPortal(false);
  const createUsageBillingUrl = useCreateUsageBillingUrl();
  const orgSummary = useGetOrganizationSummary();

  if (!orgSummary.isSuccess) {
    return null;
  }

  const missingBilling =
    missingBillings.find((b) => b.billingProvider === "stripe") ||
    missingBillings.find((b) => b.billingProvider === "shopify");

  if (!missingBilling) {
    return null;
  }

  return (
    <IgInfo
      dismissId={""}
      title={
        missingBilling.billingProvider === "stripe"
          ? "Payment Method Missing"
          : "Approve Intelligems plan"
      }
      message={
        missingBilling.billingProvider === "stripe"
          ? "Provide a payment method to continue using Intelligems."
          : "Your subscription is waiting for approval in Shopify"
      }
      severity={"error"}
      buttons={[
        <IgButton
          key={`button-1`}
          variant={"contained"}
          color={"error"}
          loading={
            getBillingPortal.isPending || createUsageBillingUrl.isPending
          }
          onClick={() => {
            setUpdatePaymentMethod(true);
            if (missingBilling.billingProvider === "stripe") {
              getBillingPortal.mutate({
                pathParams: undefined,
                queryParams: undefined,
                body: {
                  stripeCustomerId: undefined,
                  stripeSubscriptionId: missingBilling.stripeSubscriptionId,
                },
              });
            } else {
              createUsageBillingUrl.mutate({
                body: {
                  subscriptionLinkId: missingBilling.id,
                },
                queryParams: undefined,
                pathParams: undefined,
              });
            }
          }}
        >
          {missingBilling.billingProvider === "stripe"
            ? "Update Payment Method"
            : "Approve plan"}
        </IgButton>,
      ]}
    />
  );
};
